import {all, call, cancel, delay, fork, put, select, take, takeLatest} from 'redux-saga/effects'
import {push} from 'react-router-redux'
import {
  removePartImageFailure,
  removePartImageSuccess,
  types as priceListTypes,
  uploadPartImageFailure,
  uploadPartImageSuccess,
} from '../actions/priceLists'
import types from '../actions/types'
import {hideItemDrawer} from "actions/quoting"
import {snackbarClear, snackbarMessage} from "actions/snackbar"
import * as pivotTableActions from '../actions/pivotTable'
import {updateData} from 'actions/pivotTable'
import {apiErrorCodes} from 'helpers/constants'
import {createPivotTableWatcher} from './pivotTable'
import {generalError} from '../actions'
import {get, isEmpty, isNil} from 'lodash'
import {getFileFormConfig, getFileFormData, validateFileType} from 'helpers/documents'
import {findField} from 'helpers/import'
import {
  deleteEntityTypes,
  getAllFields,
  getLatestAddedPriceList,
  getPartsDeleteEntityType,
} from 'helpers/priceLists'
import {
  showDeletePartCategoryDialog,
  showDeletePartsPrompt,
  showPartUsedInPricingCalculatorPrompt,
} from 'actions/dialog'
import * as priceListActions from 'actions/priceLists'
import {selectCategory} from 'actions/priceLists'
import {loadPackTypes} from 'actions/data'
import {priceBreaksPivotTableKey} from 'reducers/priceListsContext/priceBreaks'
import {addPartPriceBreakToPartPack, getPartPriceBreaksFor, updatePartPriceBreaksWith} from 'helpers/priceBreaks'
import {SKILLS} from 'helpers/onboarding'
import * as onboardingActions from 'actions/onboarding'
import {getBasePack, getNewlyAddedPartPackId, getPartPackFor, getPartPacks, isNewPartPackAdded} from 'helpers/packs'
import {partsPivotTableKey} from 'reducers/priceListsContext/parts'
import {
  getPart,
  getSelectedPartPackId,
  getSelectedPartSupplierId,
  isComplexPricingEnabled,
  isPartSelectorOpen,
} from 'selectors/priceLists'
import {partContentsPivotTableKey} from 'reducers/priceListsContext/partContents'
import {updatePartContentsWith} from 'helpers/partContents'
import iconNames from 'theme/icons'
import {partSuppliersPivotTableKey} from 'reducers/priceListsContext/partSuppliers'
import {partSupplierCostBreaksPivotTableKey} from 'reducers/priceListsContext/partSupplierCostBreaks'
import {addPartSupplierToPart, getDefaultPartSupplier, getPartSupplierFor} from 'helpers/partSuppliers'
import {urls} from 'helpers/url'
import {addPartCostBreakFor, updatePartCostBreakFor} from 'helpers/partCostBreaks'
import {sortByBooleanKey} from 'helpers/sorting'
import {isPartTypePricingCalculator} from 'helpers/parts'
import {loadPartCategories as loadPartCategoriesAction} from 'actions/priceLists'

const importPivotTableKey = 'priceListsContext.import.pivotTable'

export const partContentFields = {
  default: true,
  part: {
    default: true,
    packs: {
      default: true,
      prices: true,
    },
    taxCode: true,
  },
  price: true,
  pack: {
    default: true,
    prices: true,
  },
}

export const partFields = {
  default: true,
  category: true,
  partImages: true,
  packs: {
    default: true,
    prices: true,
    suppliers: {
      default: true,
      costs: true,
      company: {
        name: true,
      },
    },
  },
  attributes: {
    default: true,
    option: true,
  },
  taxCode: true,
}

// The selector needs the calculator a part uses to check it is licensed
const partSelectorFields = {
  ...partFields,
  pricingCalculator: {
    name: true,
    version: true,
  },
}

export const partLoadFields = {
  default: true,
  partImages: true,
  attributes: {
    default: true,
    option: true,
  },
  category: true,
  contents: {
    default: true,
    part: {
      default: true,
      partImages: true,
      packs: true,
    },
    price: true,
    pack: true,
  },
  packs: {
    default: true,
    prices: true,
    suppliers: {
      default: true,
      company: {
        id: true,
        name: true,
      },
      costs: true,
    },
  },
  image: true,
  pricingGroupItemCategories: {
    category: {
      name: true,
    },
    pricingGroupItem: {
      discount: true,
      pricingGroup: {
        name: true,
        description: true,
      },
    },
  },
  taxCode: true,
  pricingCalculator: {
    default: true,
    inputs: {
      default: true,
      parts: {
        default: true,
        packs: true,
      },
    },
  },
}

export const pricingTemplateFields = {
  default: true,
  items: {
    default: true,
    category: true,
  },
}

export default (api) => {

  function* loadPriceLists() {
    try {
      const params = {
        showHidden: true,
      }
      const fields = {
        default: true,
        rootCategory: true,
      }

      const response = yield call(api.priceLists.get, params, fields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(priceListActions.loadPriceListsSuccess(data.result))
        },
        function* onFail(key, message, code) {
          yield put(priceListActions.loadPriceListsFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* loadPartCategories() {
    try {
      const params = {
        visible: true,
      }
      const fields = {
        default: true,
        priceList: true,
      }

      const response = yield call(api.partCategories.get, params, fields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(priceListActions.loadPartCategoriesSuccess(data.result))
        },
        function* onFail(key, message, code) {
          yield put(priceListActions.loadPartCategoryFailure(message, code))
          yield put(snackbarMessage('Failed to load categories: ' + message))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* reloadPriceLists() {
    yield put(priceListActions.loadPriceLists())
    yield put(loadPackTypes())
  }

  function* loadPartCategory({id}) {
    try {
      const response = yield call(api.partCategories.get, {id})
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(priceListActions.loadPartCategorySuccess(data.result[0]))
        },
        function* onFail(key, message, code) {
          yield put(priceListActions.loadPartCategoryFailure(message, code))
          yield put(snackbarMessage('Failed to load part category: ' + message))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* orderItemAdded({orderItem, closeDrawer}) {
    if (orderItem.partId) { // added via selector
      const selectorOpen = yield select(isPartSelectorOpen)

      if (closeDrawer) {
        // Close selector too (item drawer will close)
        if (selectorOpen) yield put(priceListActions.hideSelector())
      } else {
        // Close item drawer, but keep selector open
        yield put(hideItemDrawer())

        // If selector is not open, re-open it
        if (!selectorOpen) yield put(priceListActions.showSelector())
      }
    }
  }

  function* reloadPartPivotTable() {
    yield put(pivotTableActions.requestData(partsPivotTableKey))
  }

  function* actionFailure({message, error}) {
    try {
      yield put(snackbarMessage(message || error, iconNames.error))
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* addPart({part, loadPacks, addAnother}) {
    try {
      const response = yield call(api.parts.add, {part})

      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          const part = data.result
          yield put(priceListActions.addPartSuccess(data))
          yield put(pivotTableActions.requestData(partsPivotTableKey))

          if (loadPacks) {
            yield put(loadPackTypes())
          }
          if (!addAnother) {
            if (!isPartTypePricingCalculator(part.type)) {
              yield put(priceListActions.selectPart(part.id))
            }
            yield put(onboardingActions.skillPage(SKILLS.ADD_PART.key, 4))
            yield put(priceListActions.hideAddPartDialog())
          }
        },
        function* onFail(key, message, code) {
          yield put(priceListActions.addPartFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* duplicatePart({partId, part, loadPacks, addAnother}) {
    try {

      const params = {
        partId,
        part,
        partCategoryId: get(part, 'partCategoryId', null),
        partCode: get(part, 'partCode', null),
        packTypeDescription: get(part, 'packTypeDescription', null),
        packTypeId: get(part, 'packTypeId', null),
        msrp: get(part, 'msrp', null),
        standardCost: get(part, 'standardCost', null),
        markup: get(part, 'markup', null),
        fixedPrice: get(part, 'fixedPrice', null),
      }

      const response = yield call(api.parts.duplicate, params)

      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          const part = data.result
          yield put(priceListActions.duplicatePartSuccess(data))
          yield put(pivotTableActions.requestData(partsPivotTableKey))

          if (loadPacks)
            yield put(loadPackTypes())

          if (!addAnother) {
            yield put(priceListActions.selectPart(part.id))
            yield put(onboardingActions.skillPage(SKILLS.ADD_PART.key, 4))
            yield put(priceListActions.hideAddPartDialog())
          }
        },
        function* onFail(key, message, code) {
          yield put(priceListActions.duplicatePartFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* updateUseCostBreaks() {
    try {
      const part = yield select(getPart)
      yield call(updatePart, {part, loadPacks: false})
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* updatePart({part, loadPacks}) {
    try {
      const response = yield call(api.parts.update, {part}, partLoadFields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          const updatedPart = data.result[0]

          if (isNewPartPackAdded(part, updatedPart)) {
            const updatedPartPacks = get(updatedPart, 'packs', [])
            if (updatedPartPacks.length !== 0) {
              const newPackTypeId = getNewlyAddedPartPackId(part, updatedPart)
              yield put(priceListActions.selectPartPack(newPackTypeId, updatedPart))
            }
          }

          yield put(priceListActions.updatePartSuccess(data))
          yield put(priceListActions.updatePartPriceBreaks(updatedPart))
          yield put(priceListActions.updatePartContents(updatedPart))

          const selectedPartPackId = yield select(state => getSelectedPartPackId(state))
          yield put(priceListActions.selectPartPack(selectedPartPackId, updatedPart))

          if (loadPacks)
            yield put(loadPackTypes())

          const partPack = getPartPackFor(selectedPartPackId, updatedPart.packs)
          yield put(pivotTableActions.updateData(partSuppliersPivotTableKey, partPack.suppliers))

          const selectedPartSupplierId = yield select(state => getSelectedPartSupplierId(state))
          const partSupplier = getPartSupplierFor(updatedPart, selectedPartPackId, selectedPartSupplierId)
          if (isNil(partSupplier)) {
            const defaultPartSupplier = getDefaultPartSupplier(partPack)
            yield put(priceListActions.selectPartSupplier(get(defaultPartSupplier, 'id', null)))
          } else {
            const costs = get(partSupplier, 'costs', [])
            yield put(pivotTableActions.updateData(partSupplierCostBreaksPivotTableKey, costs))
          }
        },
        function* onFail(key, message, code) {
          yield put(priceListActions.updatePartFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* onDeleteFail(entityType, failureAction, message, code) {
    if (code === apiErrorCodes.PART_USED_IN_PRICING_CALCULATOR) {
      yield put(showPartUsedInPricingCalculatorPrompt(entityType))
    } else {
      yield put(failureAction(message, code))
    }
  }

  function* checkDeleteParts({partIds}) {
    try {
      const response = yield call(api.parts.checkDelete, {ids: partIds})
      yield api.handleResponse(
        response,
        function* onOk() {
          yield put(showDeletePartsPrompt(partIds))
        },
        function* onFail(key, message, code) {
          yield* onDeleteFail(getPartsDeleteEntityType(partIds), priceListActions.deletePartsFailure, message, code)
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* deleteParts({partIds}) {
    try {
      const response = yield call(api.parts.delete, partIds)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(priceListActions.deletePartsSuccess(partIds, data))
          yield put(pivotTableActions.requestData(partsPivotTableKey))
        },
        function* onFail(key, message, code) {
          yield* onDeleteFail(getPartsDeleteEntityType(partIds), priceListActions.deletePartsFailure, message, code)
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* selectPart({id}) {
    yield put(push(`/price-lists/parts/${id}`))
  }

  function* loadPart({id}) {
    try {

      const params = {
        id,
        search: null,
        containingCategory: null,
        category: null,
        priceList: null,
      }

      const response = yield call(api.parts.get, params, partLoadFields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(priceListActions.loadPartSuccess(data.result[0]))
          const part = yield select(state => getPart(state))

          const packs = get(part, 'packs', [])
          const basePartPack = getBasePack(packs)
          yield put(priceListActions.selectPartPack(basePartPack.id, part))

          const defaultPartSupplier = getDefaultPartSupplier(basePartPack)
          yield put(priceListActions.selectPartSupplier(isNil(defaultPartSupplier)
            ? null
            : defaultPartSupplier.id))
        },
        function* onFail(key, message, code) {
          yield put(priceListActions.loadPartFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* partQueryUsage({id}) {
    try {
      const response = yield call(api.parts.partQueryUsage, id)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(priceListActions.partQueryUsageSuccess(id, data))
          yield put(snackbarMessage('Part removed', iconNames.tick))
        },
        function* onFail(key, message, code) {
          yield put(priceListActions.partQueryUsageFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* priceListAdd({priceList, redirect}) {
    try {
      const params = {
        name: priceList.name,
        visible: priceList.visible,
        order: priceList.order,
        partTemplateId: priceList.partTemplateId,
        taxCodeId: priceList.taxCodeId,
        markup: priceList.markup,
      }

      const fields = {
        default: true,
        rootCategory: true,
      }

      const response = yield call(api.priceLists.add, params, fields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          const latestPriceList = getLatestAddedPriceList(priceList.name, data.result)
          yield put(priceListActions.addPriceListSuccess(latestPriceList))
          if (redirect) {
            yield put(priceListActions.selectCategory(latestPriceList.rootCategory))
            yield put(push(urls.priceLists.managePriceListsUrl))
            yield put(snackbarMessage(`Price list has been created`, iconNames.tick, true))
          }
        },
        function* onFail(key, message, code) {
          yield put(priceListActions.addPriceListFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* priceListUpdate({priceList}) {
    try {
      const params = {
        id: priceList.id,
        name: priceList.name,
        visible: priceList.visible,
        order: priceList.order,
        partTemplateId: priceList.partTemplateId,
        taxCodeId: priceList.taxCodeId,
        markup: priceList.markup,
        guid: priceList.guid,
      }

      const fields = {
        default: true,
        rootCategory: true,
      }

      const response = yield call(api.priceLists.update, params, fields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(priceListActions.updatePriceListSuccess(data.result))
        },
        function* onFail(key, message, code) {
          yield put(priceListActions.updatePriceListFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }


  function* importAutoDetectColumns({}) {
    try {
      // Get the job
      const job = yield select(state => state.priceListsContext.import.job)
      if (job) {

        // Auto-detect the columns from the header
        const {header, fields} = job
        if (header) {

          const {templates} = yield select(state => state.data)
          const allFields = getAllFields(templates)

          // Ensure we have a correct field array
          const newFields = [...fields]
          while (newFields.length < header.length) newFields.push(null)

          // 1st pass: Detect and allocate fields on label
          for (let i = 0; i < header.length; i++) {
            if (isNil(newFields[i]) || isEmpty(newFields[i]))
              newFields[i] = findField(header[i], allFields, newFields, false)
          }

          // 2nd pass: Detect and allocate fields on key
          for (let i = 0; i < header.length; i++) {
            if (isNil(newFields[i]) || isEmpty(newFields[i]))
              newFields[i] = findField(header[i], allFields, newFields, true)
          }

          // Re-validate with the new fields
          const newJob = {...job, fields: newFields}
          yield put(priceListActions.importJobValidate(newJob))


        }
      }
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* importJobRead({}) {
    try {
      const response = yield call(api.priceListImportJob.get, {}, {default: true})

      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(priceListActions.importJobReadSuccess(data.result[0]))
        },
        function* onFail(key, message, code) {
          if (code === apiErrorCodes.NO_RESULTS_FOUND) {
            yield put(priceListActions.importJobReadSuccess(null))
          } else {
            yield put(priceListActions.importJobReadFailure(message, code))
          }
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* importJobUpload({file, onProgress}) {
    try {
      yield put(snackbarMessage('Uploading your import file...', 'loading', false, false))

      const data = getFileFormData(file)

      const config = getFileFormConfig(progress => {
        if (onProgress) {
          const percent = (progress.loaded * 100) / progress.total
          onProgress(percent)
        }
      })

      const response = yield call(api.priceListImportJob.uploadFile, data, config)

      if (response.ok) {
        yield put(priceListActions.importJobUploadSuccess())
        yield put(priceListActions.importJobRead())
        yield put(snackbarClear())
        yield put(pivotTableActions.reset(importPivotTableKey))
        const skill = yield select(state => state.onboarding.skills.IMPORT_PRICE_LISTS)
        if (skill.active) {
          yield put(onboardingActions.skillPage(SKILLS.IMPORT_PRICE_LISTS.key, 1))
        }
      } else {
        const problem = response.headers['error'] || response.problem
        yield put(priceListActions.importJobUploadFailure(problem))
        yield put(snackbarMessage('Your file could not be uploaded: ' + problem, 'error', false))
      }
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* importJobUpdate({job, updateData = true}) {
    try {
      const params = {importJob: job}
      const response = yield call(api.priceListImportJob.update, params, {default: true})

      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(priceListActions.importJobUpdateSuccess(data.result[0]))
          if (updateData) yield put(pivotTableActions.requestData(importPivotTableKey))
        },
        function* onFail(key, message, code) {
          yield put(priceListActions.importJobUpdateFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* importJobValidate({job}) {
    try {
      const params = {importJob: job}
      const response = yield call(api.priceListImportJob.validate, params, {default: true})

      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(priceListActions.importJobValidateSuccess(data.result[0]))
        },
        function* onFail(key, message, code) {
          yield put(priceListActions.importJobValidateFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* importJobPreCommit({job}) {
    try {
      const params = {importJob: job}
      const response = yield call(api.priceListImportJob.preCommit, params, {default: true})

      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(priceListActions.importJobPreCommitSuccess(data.result[0]))

        },
        function* onFail(key, message, code) {
          yield put(priceListActions.importJobPreCommitFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* importJobCommit({job}) {
    try {
      const params = {importJob: job}
      const response = yield call(api.priceListImportJob.commit, params, {default: true})

      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(priceListActions.importJobCommitSuccess(data.result[0]))
        },
        function* onFail(key, message, code) {
          yield put(priceListActions.importJobCommitFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* importJobReset({}) {
    try {
      const response = yield call(api.priceListImportJob.reset, {}, {default: true})
      yield api.handleResponse(
        response,
        function* onOk() {
          yield put(priceListActions.importJobResetSuccess(null))
        },
        function* onFail(key, message, code) {
          if (code === apiErrorCodes.NO_RESULTS_FOUND) {
            yield put(priceListActions.importJobResetSuccess(null))
          } else {
            yield put(priceListActions.importJobResetFailure(message, code))
          }
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* redirectToPartCategory({priceListId}) {
    const fields = {
      default: true,
      rootCategory: true,
    }

    try {
      const response = yield call(api.priceLists.getOne, priceListId, fields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(priceListActions.changeTopLevelPartCategory(data.result.rootCategory))
          yield put(push(urls.priceLists.managePriceListsUrl))
        },
        function* onFail(key, message) {
          console.error(message)
          yield put(generalError(message))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* addPartCategory({partCategory}) {
    try {
      yield put(snackbarMessage('Creating your part category...', 'loading', false, false))
      const response = yield call(api.partCategories.add, {partCategory})
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(priceListActions.addPartCategorySuccess(data))
          yield put(push(urls.priceLists.managePriceListsUrl))
          yield put(snackbarMessage(
            `Part category ${data.result.name} has been created`, iconNames.tick, true
          ))
        },
        function* onFail(key, message, code) {
          yield put(priceListActions.addPartCategoryFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* updatePartCategory({partCategory}) {
    try {
      const response = yield call(api.partCategories.update, {partCategory})
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          const category = data.result[0]
          yield put(priceListActions.updatePartCategorySuccess(category))
          yield put(snackbarMessage(
            `Part category ${category.name} has been updated`, iconNames.tick, true
          ))
        },
        function* onFail(key, message, code) {
          yield put(priceListActions.updatePartCategoryFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* checkDeletePartCategory({category}) {
    try {
      const categoryId = isNil(category.id) ? category.key : category.id
      const response = yield call(api.parts.checkDelete, {categoryId})
      yield api.handleResponse(
        response,
        function* onOk() {
          yield put(showDeletePartCategoryDialog(category))
        },
        function* onFail(key, message, code) {
          yield* onDeleteFail(deleteEntityTypes.CATEGORY, priceListActions.addPartCategoryFailure, message, code)
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* deletePartCategory({id}) {
    try {
      yield put(snackbarMessage('Deleting Part Category...'))
      const response = yield call(api.partCategories.delete, {id, deleteParts: true})
      yield api.handleResponse(
        response,
        function* onOk() {
          yield put(push(urls.priceLists.managePriceListsUrl))
          yield put(snackbarMessage('Category has been deleted'))
          yield put(loadPartCategoriesAction())
        },
        function* onFail(key, message, code) {
          yield put(snackbarClear())
          yield* onDeleteFail(deleteEntityTypes.CATEGORY, priceListActions.addPartCategoryFailure, message, code)
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* deletePriceList({id}) {
    try {
      yield put(snackbarMessage('Deleting Price List...'))
      const response = yield call(api.priceLists.delete, {id})
      yield api.handleResponse(
        response,
        function* onOk() {
          yield put(priceListActions.loadPriceLists())
          yield put(snackbarMessage('Category has been deleted'))
          yield put(loadPartCategoriesAction())
        },
        function* onFail(key, message, code) {
          yield put(priceListActions.deletePriceListFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* movePartCategory({id, moveDestinationCategoryId: newCategoryId}) {
    try {
      const response = yield call(api.partCategories.delete, {id, newCategoryId})
      yield api.handleResponse(
        response,
        function* onOk() {
          yield put(loadPartCategoriesAction())
        },
        function* onFail(key, message, code) {
          yield put(priceListActions.addPartCategoryFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* selectPartCategory({data}) {
    const partCategory = data.result
    yield put(selectCategory(partCategory, true))
  }

  function* packAdd({pack}) {
    try {
      yield put(snackbarMessage('Creating your pack...', 'loading', false, false))
      const part = yield select(state => getPart(state))
      const response = yield call(api.partPacks.add, {partId: part.id, pack})
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(priceListActions.addPackSuccess(data))
          yield put(snackbarMessage(`Pack has been created`, iconNames.tick, true))
        },
        function* onFail(key, message, code) {
          yield put(priceListActions.addPackFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* deletePartPack({id}) {
    try {
      const response = yield call(api.partPacks.delete, {id})
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(priceListActions.deletePartPackSuccess(data))
          yield put(snackbarMessage('Pack Type removed', iconNames.tick))
        },
        function* onFail(key, message, code) {
          yield put(priceListActions.deletePartPackFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* partPriceBreakAdd({partPriceBreak}) {
    try {
      const response = yield call(api.partPriceBreaks.add, {partPriceBreak})
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(priceListActions.addPartPriceBreakSuccess(data))
          yield put(snackbarMessage('Price Break added', iconNames.tick))
        },
        function* onFail(key, message, code) {
          yield put(priceListActions.addPartPriceBreakFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* packTypeAdd({packType}) {
    try {
      const response = yield call(api.packTypes.add, {packType})
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(priceListActions.addPackTypeSuccess(data))
        },
        function* onFail(key, message, code) {
          yield put(priceListActions.addPackTypeFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* packTypeUpdate({packType}) {
    try {
      const response = yield call(api.packTypes.update, {packType})
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(priceListActions.updatePackTypeSuccess(data))
        },
        function* onFail(key, message, code) {
          yield put(priceListActions.updatePackTypeFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* packTypeDelete({id}) {
    try {
      const response = yield call(api.packTypes.delete, {id})
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(priceListActions.deletePackTypeSuccess(data))
        },
        function* onFail(key, message, code) {
          yield put(priceListActions.deletePackTypeFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* addPartPriceBreak({key, entity}) {
    if (key === priceBreaksPivotTableKey) {
      const {part, selectedPartPackId} = yield select(state => state.priceListsContext.part)
      const updatedPart = addPartPriceBreakToPartPack(part, selectedPartPackId, entity)
      yield put(priceListActions.updatePart(updatedPart))
    }
  }

  function* editPartPriceBreak({key, entityKey, entity}) {
    if (key === priceBreaksPivotTableKey) {
      const {part, selectedPartPackId} = yield select(state => state.priceListsContext.part)
      const updatedPart = updatePartPriceBreaksWith(entity, selectedPartPackId, entityKey, part)
      yield put(priceListActions.updatePart(updatedPart))
    }
  }

  function* updatePartPriceBreaks({part}) {
    const selectedPartPackId = yield select(state => getSelectedPartPackId(state))
    const complexPricingEnabled = yield select(isComplexPricingEnabled)
    const partPriceBreaks = getPartPriceBreaksFor(selectedPartPackId, part, complexPricingEnabled)
    yield put(pivotTableActions.updateData(priceBreaksPivotTableKey, partPriceBreaks))
  }

  function* editPartContent({key, entityKey, entity}) {
    if (key === partContentsPivotTableKey) {
      const {part} = yield select(state => state.priceListsContext.part)
      const updatedPart = updatePartContentsWith(entity, entityKey, part)
      yield put(priceListActions.updatePart(updatedPart))
    }
  }

  function* updatePartContents({}) {
    yield put(pivotTableActions.requestData(partContentsPivotTableKey))
  }

  function* addPricingTemplate({pricingTemplate}) {
    try {
      const response = yield call(api.pricingTemplates.add, {pricingTemplate}, pricingTemplateFields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(priceListActions.addPricingTemplateSuccess(data))
        },
        function* onFail(key, message, code) {
          yield put(priceListActions.addPricingTemplateFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* updatePricingTemplate({pricingTemplate}) {
    try {
      const response = yield call(api.pricingTemplates.update, {pricingTemplate}, pricingTemplateFields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(priceListActions.updatePricingTemplateSuccess(data))
        },
        function* onFail(key, message, code) {
          yield put(priceListActions.updatePricingTemplateFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* deletePricingTemplate({id}) {
    try {
      const response = yield call(api.pricingTemplates.delete, {id}, pricingTemplateFields)
      yield api.handleResponse(
        response,
        function* onOk() {
          yield put(priceListActions.deletePricingTemplateSuccess({id}))
        },
        function* onFail(key, message, code) {
          yield put(priceListActions.deletePricingTemplateFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* moveParts({partCategoryId, partIds}) {
    try {
      const params = {
        partCategoryId,
        partIds,
      }
      const response = yield call(api.parts.move, params)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(priceListActions.movePartsSuccess(data.result))
          yield put(pivotTableActions.requestData(partsPivotTableKey))
        },
        function* onFail(key, message, code) {
          yield put(priceListActions.movePartsFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  const pricingGroupFields = {
    default: true,
    companies: true,
    items: {
      default: true,
      categories: {
        default: true,
        category: true,
      },
    },
  }

  function* loadPricingGroups() {
    try {
      const params = {}
      const response = yield call(api.pricingGroups.get, params, pricingGroupFields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(priceListActions.loadPricingGroupsSuccess(data.result))
        },
        function* onFail(key, message, code) {
          yield put(priceListActions.loadPricingGroupsFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* pricingGroupAdd({pricingGroup}) {
    try {
      const params = {
        pricingGroup,
      }
      const response = yield call(api.pricingGroups.add, params, pricingGroupFields)
      yield api.handleResponse(
        response,
        function* onOk() {
          yield put(priceListActions.loadPricingGroups())
        },
        function* onFail(key, message, code) {
          yield put(priceListActions.addPricingGroupFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* pricingGroupUpdate({pricingGroup}) {
    try {
      const params = {
        pricingGroup,
      }
      const response = yield call(api.pricingGroups.update, params, pricingGroupFields)
      yield api.handleResponse(
        response,
        function* onOk() {
          yield put(priceListActions.loadPricingGroups())
        },
        function* onFail(key, message, code) {
          yield put(priceListActions.updatePricingGroupFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* pricingGroupDelete({pricingGroupId}) {
    try {
      const params = {
        id: pricingGroupId,
      }
      const fields = {
        default: true,
      }

      const response = yield call(api.pricingGroups.delete, params, fields)
      yield api.handleResponse(
        response,
        function* onOk() {
          const data = {
            id: pricingGroupId,
          }
          yield put(priceListActions.deletePricingGroupSuccess(data))
        },
        function* onFail(key, message, code) {
          yield put(priceListActions.deletePricingGroupFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  const allowedCompanyImageExtensions = ['png', 'jpg']

  function* uploadPartImage({partId, file}) {
    try {
      // Check the file type
      if (!validateFileType(file, allowedCompanyImageExtensions)) {
        const problem = 'Only png and jpg images are supported'
        yield put(uploadPartImageFailure(problem))
        yield put(snackbarMessage('The part image could not be added: ' + problem, 'error', false))
        return
      }

      yield put(snackbarMessage('Adding part image...', 'loading', false, false))
      const response = yield call(api.parts.uploadImage, partId, getFileFormData(file), getFileFormConfig())
      if (response.ok) {
        const data = response.data.data.result
        yield put(uploadPartImageSuccess(data))
        yield put(snackbarMessage('The part image has been added', iconNames.tick))
      } else {
        const problem = response.headers['error'] || response.problem
        yield put(uploadPartImageFailure(problem))
        yield put(snackbarMessage('The part image could not be added: ' + problem, 'error', false))
      }
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* removePartImage({partId}) {
    try {
      const response = yield call(api.parts.removeImage, partId)
      if (response.ok) {
        yield put(removePartImageSuccess(partId))
        yield put(snackbarMessage('This part image has been removed', iconNames.tick))
      } else {
        const problem = response.headers['error'] || response.problem
        yield put(removePartImageFailure(problem))
        yield put(snackbarMessage(
          'The part image could not be removed: ' + problem, 'error', false
        ))
      }
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* selectPartPack({id}) {
    if (!isNil(id)) {
      const part = yield select(state => getPart(state))
      const partPacks = getPartPacks(part, yield select(state => isComplexPricingEnabled(state)))
      const partPack = getPartPackFor(id, partPacks)
      const partSuppliers = get(partPack, 'suppliers', [])
        .sort((a, b) => sortByBooleanKey(a, b, 'default'))
      yield put(updateData(partSuppliersPivotTableKey, partSuppliers))
    }
  }

  function* addPartSupplier({partSupplier}) {
    const partPackId = yield select(state => getSelectedPartPackId(state))
    const {
      company,
      code,
      costCurrencyId,
      baseCostIsMin,
      baseCost,
      leadTimeDays,
      minQuantity,
      isDefault,
    } = partSupplier

    const params = {
      partSupplier: {
        partPackId,
        companyId: company.id,
        code,
        costCurrencyId,
        baseCostIsMin,
        baseCost,
        leadTimeDays,
        minQuantity,
        isDefault,
      },
    }

    try {
      yield put(snackbarMessage('Adding part supplier...', 'loading', false, false))
      const response = yield call(api.partSuppliers.add, params)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          const supplier = data.result
          yield put(priceListActions.addPartSupplierSuccess(supplier))
          yield put(snackbarMessage('Part supplier added', iconNames.tick))

          const part = yield select(state => getPart(state))
          const updatedPart = addPartSupplierToPart(
            part,
            partPackId,
            supplier,
            yield select(state => isComplexPricingEnabled(state)))

          yield put(priceListActions.updatePart(updatedPart))
          yield put(priceListActions.selectPartPack(partPackId))
          yield put(priceListActions.selectPartSupplier(supplier.id))
        },
        function* onFail(key, message, code) {
          yield put(priceListActions.addPartSupplierFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* addPartCostBreak({key, entity}) {
    if (key === partSupplierCostBreaksPivotTableKey) {
      const part = yield select(state => getPart(state))
      const partPackId = yield select(state => getSelectedPartPackId(state))
      const partSupplierId = entity.partSupplierId
      const updatedPart = addPartCostBreakFor(
        part,
        partPackId,
        partSupplierId,
        yield select(state => isComplexPricingEnabled(state)),
        entity)

      yield put(priceListActions.updatePart(updatedPart))
    }
  }

  function* editPartCostBreak({key, entity}) {
    if (key === partSupplierCostBreaksPivotTableKey) {
      const part = yield select(state => getPart(state))
      const partPackId = yield select(state => getSelectedPartPackId(state))
      const partSupplierId = entity.partSupplierId
      const updatedPart = updatePartCostBreakFor(
        part,
        partPackId,
        partSupplierId,
        yield select(state => isComplexPricingEnabled(state)),
        entity)

      yield put(priceListActions.updatePart(updatedPart))
    }
  }

  function* partSupplierSelect({id}) {
    const part = yield select(state => getPart(state))
    const partPackId = yield select(state => getSelectedPartPackId(state))
    const partSupplier = getPartSupplierFor(part, partPackId, id)
    const partSupplierCostBreaks = get(partSupplier, 'costs', [])
    yield put(updateData(partSupplierCostBreaksPivotTableKey, partSupplierCostBreaks))
  }

  function* myWatcher() {
    yield all([
      takeLatest(priceListTypes.SELECT_ALL_PRICE_LISTS, reloadPartPivotTable),
      takeLatest(priceListTypes.SELECT_PRICE_LIST, reloadPartPivotTable),
      takeLatest(priceListTypes.SELECT_CATEGORY, reloadPartPivotTable),
      takeLatest(priceListTypes.PRICE_LISTS_SELECT_PART_SUPPLIER, reloadPartPivotTable),
      takeLatest(priceListTypes.UPDATE_PART_FILTERS, reloadPartPivotTable),
      takeLatest(types.QUOTING_QUOTE_ADD_ORDER_ITEM, orderItemAdded),
      takeLatest(types.PURCHASING_PURCHASE_ORDER_ADD_ORDER_ITEM, orderItemAdded),
      takeLatest(types.PRICE_LISTS_PART_CATEGORIES_LOAD_ATTEMPT, loadPartCategories),
      takeLatest(types.PRICE_LISTS_PRICE_LIST_LOAD_ATTEMPT, loadPriceLists),
      takeLatest(types.PRICE_LISTS_PRICE_LIST_LOAD_FAILURE, actionFailure),
      takeLatest(types.PRICE_LISTS_LOAD_PART_CATEGORY_ATTEMPT, loadPartCategory),
      takeLatest(types.PRICE_LISTS_ADD_PART_ATTEMPT, addPart),
      takeLatest(types.PRICE_LISTS_ADD_PART_FAILURE, actionFailure),
      takeLatest(types.PRICE_LISTS_DUPLICATE_PART_ATTEMPT, duplicatePart),
      takeLatest(types.PRICE_LISTS_DUPLICATE_PART_FAILURE, actionFailure),
      takeLatest(types.PRICE_LISTS_UPDATE_PART_ATTEMPT, updatePart),
      takeLatest(types.PRICE_LISTS_UPDATE_PART_FAILURE, actionFailure),
      takeLatest(types.PRICE_LISTS_CHECK_DELETE_PARTS_ATTEMPT, checkDeleteParts),
      takeLatest(types.PRICE_LISTS_DELETE_PARTS_ATTEMPT, deleteParts),
      takeLatest(types.PRICE_LISTS_DELETE_PARTS_FAILURE, actionFailure),
      takeLatest(types.PRICE_LISTS_SELECT_PART, selectPart),
      takeLatest(types.PRICE_LISTS_LOAD_PART_ATTEMPT, loadPart),
      takeLatest(types.PRICE_LISTS_LOAD_PART_FAILURE, actionFailure),
      takeLatest(types.PRICE_LISTS_PART_QUERY_UPDATE_ATTEMPT, partQueryUsage),
      takeLatest(types.PRICE_LISTS_PART_QUERY_UPDATE_FAILURE, actionFailure),
      takeLatest(types.PRICE_LISTS_IMPORT_AUTO_DETECT_COLUMNS, importAutoDetectColumns),
      takeLatest(types.PRICE_LISTS_PRICE_LIST_ADD_ATTEMPT, priceListAdd),
      takeLatest(types.PRICE_LISTS_PRICE_LIST_ADD_FAILURE, actionFailure),
      takeLatest(types.PRICE_LISTS_PRICE_LIST_UPDATE_ATTEMPT, priceListUpdate),
      takeLatest(types.PRICE_LISTS_PRICE_LIST_UPDATE_FAILURE, actionFailure),
      takeLatest(types.PRICE_LISTS_IMPORT_JOB_READ_ATTEMPT, importJobRead),
      takeLatest(types.PRICE_LISTS_IMPORT_JOB_UPLOAD_ATTEMPT, importJobUpload),
      takeLatest(types.PRICE_LISTS_IMPORT_JOB_UPDATE_ATTEMPT, importJobUpdate),
      takeLatest(types.PRICE_LISTS_IMPORT_JOB_VALIDATE_ATTEMPT, importJobValidate),
      takeLatest(types.PRICE_LISTS_IMPORT_JOB_PRECOMMIT_ATTEMPT, importJobPreCommit),
      takeLatest(types.PRICE_LISTS_IMPORT_JOB_COMMIT_ATTEMPT, importJobCommit),
      takeLatest(types.PRICE_LISTS_IMPORT_JOB_RESET_ATTEMPT, importJobReset),
      takeLatest(types.PRICE_LISTS_IMPORT_JOB_RESET_SUCCESS, reloadPriceLists),
      takeLatest(types.PRICE_LISTS_PART_CATEGORY_ADD_ATTEMPT, addPartCategory),
      takeLatest(types.PRICE_LISTS_PART_CATEGORY_ADD_FAILURE, actionFailure),
      takeLatest(types.PRICE_LISTS_PART_CATEGORY_ADD_SUCCESS, selectPartCategory),
      takeLatest(types.PRICE_LISTS_PART_CATEGORY_UPDATE_ATTEMPT, updatePartCategory),
      takeLatest(types.PRICE_LISTS_PART_CATEGORY_UPDATE_FAILURE, actionFailure),
      takeLatest(types.PRICE_LISTS_PART_CATEGORY_CHECK_DELETE_ATTEMPT, checkDeletePartCategory),
      takeLatest(types.PRICE_LISTS_PART_CATEGORY_DELETE_ATTEMPT, deletePartCategory),
      takeLatest(types.PRICE_LISTS_PRICE_LIST_DELETE_ATTEMPT, deletePriceList),
      takeLatest(types.PRICE_LISTS_PRICE_LIST_DELETE_FAILURE, actionFailure),
      takeLatest(types.PRICE_LISTS_PART_CATEGORY_MOVE_ATTEMPT, movePartCategory),
      takeLatest(types.PRICE_LISTS_PART_CATEGORY_DELETE_FAILURE, actionFailure),
      takeLatest(types.PRICE_LISTS_PACK_ADD_ATTEMPT, packAdd),
      takeLatest(types.PRICE_LISTS_PACK_ADD_FAILURE, actionFailure),
      takeLatest(types.PRICE_LISTS_PART_PACK_DELETE_ATTEMPT, deletePartPack),
      takeLatest(types.PRICE_LISTS_PART_PACK_DELETE_FAILURE, actionFailure),
      takeLatest(types.PRICE_LISTS_PART_PRICE_BREAK_ADD_ATTEMPT, partPriceBreakAdd),
      takeLatest(types.PRICE_LISTS_PART_PRICE_BREAK_ADD_FAILURE, actionFailure),
      takeLatest(types.PRICE_LISTS_PACK_TYPE_ADD_ATTEMPT, packTypeAdd),
      takeLatest(types.PRICE_LISTS_PACK_TYPE_ADD_FAILURE, actionFailure),
      takeLatest(types.PRICE_LISTS_PACK_TYPE_UPDATE_ATTEMPT, packTypeUpdate),
      takeLatest(types.PRICE_LISTS_PACK_TYPE_UPDATE_FAILURE, actionFailure),
      takeLatest(types.PRICE_LISTS_PACK_TYPE_DELETE_ATTEMPT, packTypeDelete),
      takeLatest(types.PRICE_LISTS_PACK_TYPE_DELETE_FAILURE, actionFailure),
      takeLatest(types.PIVOT_TABLE_ADD_ENTITY, addPartPriceBreak),
      takeLatest(types.PIVOT_TABLE_EDIT_DATA, editPartPriceBreak),
      takeLatest(types.PIVOT_TABLE_EDIT_DATA, editPartContent),
      takeLatest(types.PRICE_LISTS_UPDATE_PART_PRICE_BREAKS, updatePartPriceBreaks),
      takeLatest(types.PRICE_LISTS_UPDATE_PART_CONTENTS, updatePartContents),
      takeLatest(types.PRICE_LISTS_ADD_PRICING_TEMPLATE_ATTEMPT, addPricingTemplate),
      takeLatest(types.PRICE_LISTS_UPDATE_PRICING_TEMPLATE_ATTEMPT, updatePricingTemplate),
      takeLatest(types.PRICE_LISTS_DELETE_PRICING_TEMPLATE_ATTEMPT, deletePricingTemplate),
      takeLatest(types.PRICE_LISTS_REDIRECT_TO_PART_CATEGORY, redirectToPartCategory),
      takeLatest(types.PRICE_LISTS_MOVE_PARTS_ATTEMPT, moveParts),
      takeLatest(types.PRICE_LISTS_MOVE_PARTS_FAILURE, actionFailure),
      takeLatest(types.PRICE_LISTS_PRICING_GROUPS_LOAD_ATTEMPT, loadPricingGroups),
      takeLatest(types.PRICE_LISTS_PRICING_GROUPS_LOAD_FAILURE, actionFailure),
      takeLatest(types.PRICE_LISTS_PRICING_GROUPS_ADD_ATTEMPT, pricingGroupAdd),
      takeLatest(types.PRICE_LISTS_PRICING_GROUPS_ADD_FAILURE, actionFailure),
      takeLatest(types.PRICE_LISTS_PRICING_GROUPS_UPDATE_ATTEMPT, pricingGroupUpdate),
      takeLatest(types.PRICE_LISTS_PRICING_GROUPS_UPDATE_FAILURE, actionFailure),
      takeLatest(types.PRICE_LISTS_PRICING_GROUPS_DELETE_ATTEMPT, pricingGroupDelete),
      takeLatest(types.PRICE_LISTS_PRICING_GROUPS_DELETE_FAILURE, actionFailure),
      takeLatest(types.PRICE_LISTS_PART_UPLOAD_IMAGE_ATTEMPT, uploadPartImage),
      takeLatest(types.PRICE_LISTS_PART_REMOVE_IMAGE_ATTEMPT, removePartImage),
      takeLatest(types.PRICE_LISTS_SELECT_PART_PACK, selectPartPack),
      takeLatest(types.PRICE_LISTS_PART_SUPPLIER_ADD_ATTEMPT, addPartSupplier),
      takeLatest(types.PIVOT_TABLE_ADD_ENTITY, addPartCostBreak),
      takeLatest(types.PIVOT_TABLE_EDIT_DATA, editPartCostBreak),
      takeLatest(types.PRICE_LISTS_SELECT_PART_SUPPLIER, partSupplierSelect),
      takeLatest(types.PRICE_LISTS_CHANGE_USE_COST_BREAKS, updateUseCostBreaks),
    ])
  }

  function* getImportJob() {
    const response = yield call(api.priceListImportJob.get, {}, {default: true})
    if (response.ok) {
      const job = get(response, 'data.data[0].result[0]')
      if (job) yield put(priceListActions.importJobReadSuccess(job))
      return job
    }
    return null
  }

  // deamon thread to watch for completed import job
  function* checkForCompleteImportJob() {

    // read the job
    let job = yield getImportJob()
    while (job && !job.completed) {
      yield delay(1000)
      job = yield getImportJob()
    }

    // emit a 'complete' action
    yield put({type: types.PRICE_LISTS_IMPORT_JOB_COMPLETE})
  }

  // deamon thread to watch for pre-commit process running on server
  function* importJobPreCommitWatcher() {
    try {
      while (yield take(types.PRICE_LISTS_IMPORT_JOB_PRECOMMIT_SUCCESS)) {

        // check for job completion
        const tasks = yield all([fork(checkForCompleteImportJob)])

        // wait for job completion
        yield take(types.PRICE_LISTS_IMPORT_JOB_COMPLETE)

        // cancel the task
        yield all(tasks.map(task => cancel(task)))
      }
    } catch (e) {
      console.error(e)
    }
  }

  // deamon thread to watch for commit process running on server
  function* importJobCommitWatcher() {
    try {
      while (yield take(types.PRICE_LISTS_IMPORT_JOB_COMMIT_SUCCESS)) {

        // check for job completion
        const tasks = yield all([fork(checkForCompleteImportJob)])

        // wait for job completion
        yield take(types.PRICE_LISTS_IMPORT_JOB_COMPLETE)

        // cancel the task
        yield all(tasks.map(task => cancel(task)))
      }
    } catch (e) {
      console.error(e)
    }
  }

  const partsPivotTable = createPivotTableWatcher(
    api.parts,
    partsPivotTableKey,
    [
      // ... additional listening actions
      priceListTypes.SELECT_ALL_PRICE_LISTS,
      priceListTypes.SELECT_PRICE_LIST,
      priceListTypes.SELECT_CATEGORY,
      priceListTypes.UPDATE_PART_FILTERS,
    ],
    state => {
      const {priceListId, categoryId, parts, showDormants, partSupplierCompanyId} = state.priceListsContext.parts

      const params = {}

      if (isNil(parts.search) || parts.search.length === 0) {
        if (priceListId) params.priceList = priceListId
        if (categoryId) params.containingCategory = categoryId
        if (partSupplierCompanyId) params.partSupplierCompanyId = partSupplierCompanyId
      }

      const search = get(parts, 'search', '')
      if (!isEmpty(search)) {
        if (priceListId) params.priceList = priceListId
        if (categoryId) params.containingCategory = categoryId
      }

      if (!showDormants)
        params.activeSale = true

      return params
    },
    partSelectorFields
  )

  const importPivotTable = createPivotTableWatcher(
    api.priceListImportData,
    importPivotTableKey,
    [
      types.PRICE_LISTS_IMPORT_UPDATE_FILTERS,
      types.PRICE_LISTS_IMPORT_JOB_UPLOAD_SUCCESS,
    ],
    state => ({
      ...state.priceListsContext.import.filters,
      includeCommitValidation: (state.priceListsContext.import.currentStep > 1),
    })
  )

  const priceBreaksPivotTableWatcher = createPivotTableWatcher(
    api.partPriceBreaks,
    priceBreaksPivotTableKey,
    [
      types.PRICE_LISTS_SELECT_PART_PACK,
    ],
    state => {
      return {
        partPackId: getSelectedPartPackId(state),
        noCompany: true,
      }
    },
    {
      default: true,
    }
  )

  const partContentsPivotTableWatcher = createPivotTableWatcher(
    api.partContents,
    partContentsPivotTableKey,
    [
      types.PRICE_LISTS_LOAD_PART_SUCCESS,
    ],
    state => {
      return {
        parentPartId: getPart(state).id,
      }
    },
    partContentFields
  )

  const partSuppliersPriceBreaksPivotTableWatcher = createPivotTableWatcher(
    api.partCostBreaks,
    partSupplierCostBreaksPivotTableKey,
    [],
    state => {
      return {
        partSupplierId: getSelectedPartSupplierId(state),
      }
    },
    {
      default: true,
    }
  )

  function* watcher() {
    yield fork(myWatcher)
    yield fork(partsPivotTable)
    yield fork(priceBreaksPivotTableWatcher)
    yield fork(partContentsPivotTableWatcher)
    yield fork(partSuppliersPriceBreaksPivotTableWatcher)
    yield fork(importPivotTable)
    yield fork(importJobPreCommitWatcher)
    yield fork(importJobCommitWatcher)
  }

  return {
    watcher,
  }
}
